import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import DashboardMock from "./DashboardMock";
import { scrollToId } from "./scroll";

const EASE = [0.16, 1, 0.3, 1];

const HEADLINE = [
  { text: "B2B Growth,", gradient: false },
  { text: "Powered by AI", gradient: true },
];

export default function Hero() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const dashY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const dashRotate = useTransform(scrollYProgress, [0, 1], [0, 5]);
  const copyY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section
      id="top"
      ref={heroRef}
      className="relative overflow-hidden pb-24 pt-40 md:pt-48"
      data-testid="hero-section"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-cyan-600/20 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-64 h-[420px] w-[420px] rounded-full bg-teal-700/15 blur-[120px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 top-96 h-[420px] w-[420px] rounded-full bg-blue-700/15 blur-[120px]"
      />

      <motion.div
        style={{ y: copyY, opacity: copyOpacity }}
        className="relative mx-auto max-w-5xl px-6 text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="mx-auto mb-8 flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 backdrop-blur-md"
          data-testid="hero-badge"
        >
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-teal-400" />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-200/90">
            AI-Powered B2B Growth Engine
          </span>
          <Sparkles className="h-3.5 w-3.5 text-teal-300" />
        </motion.div>

        <h1
          className="font-display text-5xl font-semibold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl"
          data-testid="hero-headline"
        >
          {HEADLINE.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.25 + i * 0.13, ease: EASE }}
                className={`block ${
                  line.gradient
                    ? "bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent"
                    : ""
                }`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
          className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg"
          data-testid="hero-subheadline"
        >
          Let AI-driven automation find, engage, and convert prospective clients
          for your startup or e-commerce store 24/7.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="mt-10 flex flex-col items-center gap-3.5"
        >
          <button
            onClick={() => scrollToId("#pricing")}
            className="group relative flex items-center gap-2.5 rounded-full bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 px-9 py-4 text-base font-semibold text-white shadow-[0_0_44px_rgba(34,211,238,0.45)] transition-[transform,box-shadow] duration-300 hover:scale-[1.04] hover:shadow-[0_0_64px_rgba(45,212,191,0.6)]"
            data-testid="hero-cta-button"
          >
            Start Free Trial
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <p
            className="text-xs font-medium tracking-wide text-slate-500"
            data-testid="hero-cta-subtext"
          >
            No credit card required &bull; Cancel anytime
          </p>
        </motion.div>
      </motion.div>

      <div
        className="relative mx-auto mt-20 max-w-6xl px-6"
        style={{ perspective: 1400 }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-600/20 blur-[110px]"
        />
        <motion.div
          style={{ y: dashY, rotate: dashRotate, transformStyle: "preserve-3d" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 80, rotateX: 22 }}
            animate={{ opacity: 1, y: 0, rotateX: 8 }}
            transition={{ duration: 1.3, delay: 1.05, ease: EASE }}
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            >
              <DashboardMock />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
